import { appendPostingSet, appendReversalsForPaymentLine } from './ledgerService';
import { toCents, fromCents } from './money';

// Helper expects a contract object with beneficiary company and vendor/partner allocations available on the line

export const postInvoicedVendorCredits = async ({ contractId, paymentLine, createdBy }) => {
  // paymentLine should include vendorComponents: [{ vendorId, amountUsd, subAttribution? }]
  const entries = (paymentLine.vendorComponents || []).map((vc) => ({
    amountUsd: Number(vc.amountUsd),
    directAccountType: 'vendor',
    directAccountId: vc.vendorId,
    subAccount: vc.subAttribution || null,
    serviceId: vc.serviceId || null,
    serviceName: vc.serviceName || '',
    eventType: 'Invoiced',
    memo: vc.serviceName ? `Service: ${vc.serviceName}` : (vc.serviceId ? `Service: ${vc.serviceId}` : 'Service'),
  }));
  if (entries.length === 0) return [];
  return appendPostingSet(entries, {
    source: 'Contract',
    memo: `Invoice posted for line ${paymentLine.id}`,
    contractId,
    paymentLineId: paymentLine.id,
    milestoneId: paymentLine.milestoneId || null,
    createdBy,
  });
};

export const postPaidCredits = async ({ contractId, paymentLine, partnerCommissionUsd = 0, processingUsd = 0, serviceMargins = [], transportUsd = 0, partnerRoles = [], beneficiaryCompanyId, createdBy }) => {
  const entries = [];
  
  const lineAmountCents = toCents(paymentLine.lineAmountUsd || 0);
  const partnerCommissionCents = toCents(partnerCommissionUsd || 0);
  const transportCents = toCents(transportUsd || 0);
  const processingCents = toCents(processingUsd || 0);
  
  // 🔍 RACE CONDITION LOG: Track posting input parameters
  console.log('[postPaidCredits] Input:', {
    timestamp: new Date().toISOString(),
    contractId,
    paymentLineId: paymentLine.id,
    lineAmountUsd: paymentLine.lineAmountUsd,
    partnerCommissionUsd,
    processingUsd,
    serviceMargins,
    transportUsd,
    lineAmountCents,
    partnerCommissionCents,
    transportCents,
    processingCents,
    partnerRolesCount: (partnerRoles || []).length,
    partnerRoles: partnerRoles,
    beneficiaryCompanyId,
  });

  const roles = Array.isArray(partnerRoles) ? partnerRoles : [];
  
  // 1. Company gets margin per service (separate entry for each)
  (serviceMargins || []).forEach((sm) => {
    const marginCents = toCents(sm.marginUsd || 0);
    if (marginCents > 0 && beneficiaryCompanyId) {
      entries.push({
        amountUsd: fromCents(marginCents),
        directAccountType: 'company',
        directAccountId: beneficiaryCompanyId,
        eventType: 'Paid',
        memo: sm.serviceName ? `Margin: ${sm.serviceName}` : 'Margin'
      });
      console.log('[postPaidCredits] Added service margin:', { service: sm.serviceName, amount: fromCents(marginCents) });
    }
  });
  
  // 2. Company gets the transport fee (separate)
  if (transportCents > 0 && beneficiaryCompanyId) {
    entries.push({
      amountUsd: fromCents(transportCents),
      directAccountType: 'company',
      directAccountId: beneficiaryCompanyId,
      eventType: 'Paid',
      memo: 'Transport Fee'
    });
    console.log('[postPaidCredits] Added company transport:', fromCents(transportCents));
  }
  
  // 2b. Company gets the processing fee (separate)
  if (processingCents > 0 && beneficiaryCompanyId) {
    entries.push({
      amountUsd: fromCents(processingCents),
      directAccountType: 'company',
      directAccountId: beneficiaryCompanyId,
      eventType: 'Paid',
      memo: 'Processing : Fee'
    });
    console.log('[postPaidCredits] Added company processing fee:', fromCents(processingCents));
  }
  
  // 3. Each partner (INCLUDING company role) gets their % of the SERVICES BASE (not full invoice)
  const validRoles = roles.filter(r => r?.entityId && Number(r?.percent) > 0);
  
  console.log('[postPaidCredits] Processing partner roles:', {
    totalRoles: roles.length,
    validRolesCount: validRoles.length,
    partnerCommissionCents,
    validRoles: validRoles.map(r => ({ 
      positionId: r?.positionId, 
      entityId: r?.entityId, 
      percent: r?.percent,
    })),
  });
  
  // Calculate commissions based on total expected commission amount
  const totalPercent = validRoles.reduce((sum, r) => sum + (Number(r?.percent) || 0), 0);
  let totalCents = 0;
  const roleShares = totalPercent > 0 ? validRoles.map((r) => {
    const percentValue = Number(r?.percent) || 0;
    const cents = Math.floor(partnerCommissionCents * (percentValue / totalPercent));
    totalCents += cents;
    return { role: r, cents };
  }) : [];
  
  // Handle rounding - distribute leftover cents
  let leftoverCents = partnerCommissionCents - totalCents;
  const sortedForLeftover = [...roleShares].sort((a, b) => b.cents - a.cents);
  for (const ps of sortedForLeftover) {
    if (leftoverCents <= 0) break;
    ps.cents += 1;
    leftoverCents -= 1;
  }
  
  roleShares.forEach(({ role: r, cents }) => {
    const usd = fromCents(cents);
    
    console.log('[postPaidCredits] Role calculation:', { 
      positionId: r.positionId, 
      partnerCommissionCents,
      percent: Number(r?.percent) || 0,
      cents, 
      usd 
    });
    
    if (usd !== 0) {
      const isCompanyRole = String(r?.positionId || '') === 'company';
      const roleLabel = (r?.positionName && String(r.positionName).trim())
        ? String(r.positionName).trim()
        : (r?.positionId ? String(r.positionId).replace(/_/g, ' ') : '');
      
      entries.push({
        amountUsd: usd,
        directAccountType: isCompanyRole ? 'company' : 'partner',
        directAccountId: r.entityId,
        subAccount: r.subAccountId || null,
        eventType: 'Paid',
        memo: `Commission: ${roleLabel}`
      });
      console.log('[postPaidCredits] Added entry:', { type: isCompanyRole ? 'company' : 'partner', entityId: r.entityId, usd, roleLabel });
    }
  });
  
  // Summary
  const totalServiceMarginCents = (serviceMargins || []).reduce((sum, sm) => sum + toCents(sm.marginUsd || 0), 0);
  const totalCommissionCents = roleShares.reduce((sum, rs) => sum + rs.cents, 0);
  const totalPostedCents = totalServiceMarginCents + transportCents + processingCents + totalCommissionCents;
  
  // 🔍 RACE CONDITION LOG: Track final distribution and detect over-posting
  const totalPostedUsd = fromCents(totalPostedCents);
  const lineAmountUsd = fromCents(lineAmountCents);
  const overPostAmount = totalPostedUsd - lineAmountUsd;
  
  console.log('[postPaidCredits] Distribution summary:', {
    timestamp: new Date().toISOString(),
    contractId,
    paymentLineId: paymentLine.id,
    invoiceCents: lineAmountCents,
    invoiceUsd: lineAmountUsd,
    partnerCommissionCents,
    serviceMarginsCents: totalServiceMarginCents,
    transportCents,
    processingCents,
    commissionsCents: totalCommissionCents,
    totalPostedCents,
    totalPostedUsd,
    overPostAmount,
    isOverPosted: overPostAmount > 0.01,
    WARNING: overPostAmount > 0.01 ? '⚠️ OVER-POSTING DETECTED!' : null
  });

  // Debug: log final entries
  console.log('[postPaidCredits] Final entries:', {
    timestamp: new Date().toISOString(),
    count: entries.length,
    entries: entries.map(e => ({ type: e.directAccountType, entityId: e.directAccountId, amount: e.amountUsd, memo: e.memo })),
  });

  if (entries.length === 0) {
    console.log('[postPaidCredits] No entries to post, returning empty');
    return [];
  }
  return appendPostingSet(entries, { source: 'Contract', memo: `Paid posting for line ${paymentLine.id}`, contractId, paymentLineId: paymentLine.id, createdBy });
};

export const postPartialCredits = async ({ contractId, paymentLine, appliedUsd, totalUsd, partnerRoles = [], beneficiaryCompanyId, createdBy, partnerCommissionUsd = 0, processingUsd = 0, serviceMargins = [], transportUsd = 0 }) => {
  const appliedCents = toCents(appliedUsd);
  const totalCents = toCents(totalUsd);
  const partnerCommissionCents = toCents(partnerCommissionUsd || 0);
  if (totalCents <= 0 || appliedCents <= 0) return [];
  
  // Pro-rata based on portion paid
  const ratio = appliedCents / totalCents;
  const entries = [];
  
  const transportCents = toCents(transportUsd || 0);
  const processingCents = toCents(processingUsd || 0);
  const roles = Array.isArray(partnerRoles) ? partnerRoles : [];
  
  // 1. Company gets pro-rata margin per service
  (serviceMargins || []).forEach((sm) => {
    const marginCents = toCents(sm.marginUsd || 0);
    const proRataMarginCents = Math.round(marginCents * ratio);
    if (proRataMarginCents > 0 && beneficiaryCompanyId) {
      entries.push({
        amountUsd: fromCents(proRataMarginCents),
        directAccountType: 'company',
        directAccountId: beneficiaryCompanyId,
        eventType: 'Partial',
        memo: sm.serviceName ? `Service Margin: ${sm.serviceName}` : 'Service Margin'
      });
    }
  });
  
  // 2. Company gets pro-rata transport
  const proRataTransportCents = Math.round(transportCents * ratio);
  if (proRataTransportCents > 0 && beneficiaryCompanyId) {
    entries.push({
      amountUsd: fromCents(proRataTransportCents),
      directAccountType: 'company',
      directAccountId: beneficiaryCompanyId,
      eventType: 'Partial',
      memo: 'Transport Fee'
    });
  }
  
  // 2b. Company gets pro-rata processing fee
  const proRataProcessingCents = Math.round(processingCents * ratio);
  if (proRataProcessingCents > 0 && beneficiaryCompanyId) {
    entries.push({
      amountUsd: fromCents(proRataProcessingCents),
      directAccountType: 'company',
      directAccountId: beneficiaryCompanyId,
      eventType: 'Partial',
      memo: 'Processing : Fee'
    });
  }
  
  // 3. All partners (including company role) get pro-rata of total commission
  const validRoles = roles.filter(r => r?.entityId && Number(r?.percent) > 0);
  
  const totalPercent = validRoles.reduce((sum, r) => sum + (Number(r?.percent) || 0), 0);
  const proRataCommissionCents = Math.round(partnerCommissionCents * ratio);
  const roleShares = totalPercent > 0 ? validRoles.map((r) => {
    const percentValue = Number(r?.percent) || 0;
    const cents = Math.floor(proRataCommissionCents * (percentValue / totalPercent));
    return { role: r, cents };
  }) : [];
  
  let leftoverCents = proRataCommissionCents - roleShares.reduce((sum, rs) => sum + rs.cents, 0);
  const sortedForLeftover = [...roleShares].sort((a, b) => b.cents - a.cents);
  for (const ps of sortedForLeftover) {
    if (leftoverCents <= 0) break;
    ps.cents += 1;
    leftoverCents -= 1;
  }
  
  roleShares.forEach(({ role: r, cents }) => {
    const usd = fromCents(cents);
    
    if (usd !== 0) {
      const isCompanyRole = String(r?.positionId || '') === 'company';
      const roleLabel = (r?.positionName && String(r.positionName).trim())
        ? String(r.positionName).trim()
        : (r?.positionId ? String(r.positionId).replace(/_/g, ' ') : '');
      
      entries.push({
        amountUsd: usd,
        directAccountType: isCompanyRole ? 'company' : 'partner',
        directAccountId: r.entityId,
        subAccount: r.subAccountId || null,
        eventType: 'Partial',
        memo: `Commission: ${roleLabel}`
      });
    }
  });

  if (entries.length === 0) return [];
  return appendPostingSet(entries, { source: 'Contract', memo: `Partial posting for line ${paymentLine.id}`, contractId, paymentLineId: paymentLine.id, createdBy });
};

export const postCancellationReversals = async ({ paymentLineId, createdBy }) => {
  return appendReversalsForPaymentLine({ paymentLineId, createdBy, memo: 'Contract line cancelled' });
};

export default { postInvoicedVendorCredits, postPaidCredits, postPartialCredits, postCancellationReversals };


